import { useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuthStore } from '../store/authStore'
import { exchangeAuthCode } from '../lib/api'
import { Zap } from 'lucide-react'

/**
 * Landing page after OAuth redirect (Slack, Teams, SSO).
 * Exchanges one-time ?code= for JWT pair (tokens never stay in the URL).
 */
export function AuthCallbackPage() {
  const [params] = useSearchParams()
  const login = useAuthStore(s => s.login)
  const navigate = useNavigate()

  useEffect(() => {
    const code = params.get('code')
    const legacyToken = params.get('token')
    const legacyRefresh = params.get('refresh_token')

    async function run() {
      if (code) {
        try {
          const data = await exchangeAuthCode(code)
          await login(data.access_token, data.refresh_token)
          navigate('/dashboard', { replace: true })
          return
        } catch {
          navigate('/login', { replace: true })
          return
        }
      }

      // Legacy redirect (pre–auth-code exchange); strip query via replace navigation.
      if (legacyToken) {
        try {
          await login(legacyToken, legacyRefresh ?? undefined)
          navigate('/dashboard', { replace: true })
        } catch {
          navigate('/login', { replace: true })
        }
        return
      }

      navigate('/login', { replace: true })
    }

    void run()
  }, [params, login, navigate])

  return (
    <div className="min-h-screen bg-warm-base flex flex-col items-center justify-center gap-4">
      <div className="w-14 h-14 rounded-2xl bg-brand-teal flex items-center justify-center shadow-glow-teal animate-pulse">
        <Zap size={22} className="text-white" />
      </div>
      <p className="text-ink-muted text-sm">Signing you in…</p>
    </div>
  )
}
